import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
} from 'react'
import { LinkContainer } from 'react-router-bootstrap'

import LoadingContext from '../../context/Loading/Loading'

import Tab from 'react-bootstrap/Tab'
import Tabs from 'react-bootstrap/Tabs'
import Card from 'react-bootstrap/Card'
import Stack from 'react-bootstrap/Stack'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Table from 'react-bootstrap/Table'
import Form from 'react-bootstrap/Form'
import FloatingLabel from 'react-bootstrap/FloatingLabel'

import { usePagination } from '../../providers/PaginationProvider/PaginationProvider'
import useDebouncedValue from '../../utilities/useDebouncedValue'
import AuthService from '../../utilities/services/auth.service'

import {
  deleteAccessPassTemplate,
  getAccessPasses,
  getAccessPassTemplates,
  updateAccessPassTemplate,
} from '../../utilities/api'
import {
  exportData,
  formatPhoneNumber,
  capitalizeString,
  formatTicketLocationValue,
} from '../../utilities/helpers'
import moment from 'moment'

import { EmptyContainer } from '../EmptyContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { SearchBar } from '../SearchBar'
import { Pagination } from '../Pagination'
import { CreateAccessPassModal } from './CreateAccessPassModal'
import { CreateAccessPassTemplateModal } from './CreateAccessPassTemplateModal'
import { EditAccessPassModal } from './EditAccessPassModal'

const PASS_STATUS_OPTS = [
  { label: 'Active', value: 'active' },
  { label: 'Revoked', value: 'revoked' },
  { label: 'All', value: 'all' },
]

function filterByQuery(items, query, fields) {
  const normalized = query.trim().toLowerCase()
  if (!normalized) return items

  return items.filter((item) =>
    fields.some((getValue) =>
      String(getValue(item) || '')
        .toLowerCase()
        .includes(normalized)
    )
  )
}

function formatSeat(pass) {
  const fallback = pass?.generalAdmission ? 'GA' : ''
  const parts = [
    formatTicketLocationValue(pass?.sectionNumber, fallback),
    formatTicketLocationValue(pass?.rowNumber, fallback),
    formatTicketLocationValue(pass?.seatNumber, fallback),
  ].filter(Boolean)
  return parts.length ? parts.join(' / ') : ''
}

function holderDisplayName(pass) {
  const fromHolder = pass?.holder?.fullName
  if (fromHolder) return fromHolder
  const user = pass?.users_permissions_user
  const fromUser = `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
  if (fromUser) return fromUser
  return pass?.name || ''
}

function holderEmail(pass) {
  return (
    pass?.holder?.email ||
    pass?.email ||
    pass?.users_permissions_user?.email ||
    ''
  )
}

function toAccessPassHolderExportRow(pass) {
  const name = holderDisplayName(pass)
  const [firstName = '', ...rest] = String(name || '')
    .trim()
    .split(/\s+/)
  const lastName = rest.join(' ')

  return {
    pass_name: pass?.name || '',
    check_in_code: pass?.checkInCode || '',
    first_name: firstName,
    last_name: lastName,
    name,
    email: holderEmail(pass),
    phone_number:
      pass?.holder?.phoneNumber ||
      pass?.users_permissions_user?.phoneNumber ||
      pass?.order?.phoneNumber ||
      '',
    address: pass?.holder?.address || pass?.address || '',
    events: pass?.events?.length || 0,
    seat: formatSeat(pass),
    status: pass?.status || '',
    order_id: pass?.order?.orderId || '',
    issued: pass?.createdAt ? moment(pass.createdAt).format('MMM D, YYYY') : '',
  }
}

function PaginatedTablePanel({
  items,
  query,
  setQuery,
  searchPlaceholder,
  emptyTitle,
  emptyDescription,
  noResultsTitle,
  columns,
  renderRow,
  filters,
}) {
  const debouncedQuery = useDebouncedValue(query, 300)
  const {
    currentPage,
    setCurrentPage,
    perPage,
    pageCount,
    setPageCount,
    resultsPerPageOpts,
    handleChange,
  } = usePagination()

  const filteredItems = useMemo(
    () =>
      filterByQuery(
        items,
        debouncedQuery,
        columns.map((col) => col.searchValue)
      ),
    [items, debouncedQuery, columns]
  )

  const paginatedItems = useMemo(() => {
    const start = currentPage * perPage
    return filteredItems.slice(start, start + perPage)
  }, [filteredItems, currentPage, perPage])

  useEffect(() => {
    setPageCount(Math.max(1, Math.ceil(filteredItems.length / perPage)))
  }, [filteredItems.length, perPage, setPageCount])

  useEffect(() => {
    setCurrentPage(0)
  }, [debouncedQuery, setCurrentPage])

  if (items.length === 0) {
    return (
      <Card body>
        <EmptyContainer style="center lg">
          <p>{emptyTitle}</p>
          {emptyDescription ? (
            <p className="small text-secondary mb-0">{emptyDescription}</p>
          ) : null}
        </EmptyContainer>
      </Card>
    )
  }

  return (
    <>
      <Card body className="card--md">
        <Stack direction="horizontal" gap={3} className="mb-4">
          <div className="flex-grow-1">
            <SearchBar getQuery={setQuery} placeholder={searchPlaceholder} />
          </div>
          {filters}
        </Stack>

        {filteredItems.length === 0 ? (
          <EmptyContainer style="center lg">
            <p>{noResultsTitle}</p>
            <p className="small text-secondary mb-0">
              Try adjusting your search or filters.
            </p>
          </EmptyContainer>
        ) : (
          <div className="table-container border-bottom-0">
            <div className="table-container--fixed-height">
              <Table responsive className="mb-0">
                <thead>
                  <tr className="md md--scroll">
                    {columns.map((column) => (
                      <th
                        key={column.key}
                        className={`text-left uppercase text-body ${column.className || ''}`}
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>{paginatedItems.map((item) => renderRow(item))}</tbody>
              </Table>
            </div>
          </div>
        )}
      </Card>

      {filteredItems.length > 0 ? (
        <Pagination
          pageCount={pageCount}
          perPage={perPage}
          currentPage={currentPage}
          resultsPerPageOpts={resultsPerPageOpts}
          handleClick={(value) => setCurrentPage(value.selected)}
          handleChange={handleChange}
        />
      ) : null}
    </>
  )
}

export default function AccessPassesWrapper({ organizationUUID }) {
  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)
  const organization = AuthService.getOrg()?.[0]

  const [passes, setPasses] = useState([])
  const [templates, setTemplates] = useState([])
  const [activeTab, setActiveTab] = useState('passes')
  const [passQuery, setPassQuery] = useState('')
  const [templateQuery, setTemplateQuery] = useState('')
  const [passStatus, setPassStatus] = useState('active')
  const [showCreate, setShowCreate] = useState(false)
  const [showCreateTemplate, setShowCreateTemplate] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState(null)
  const [editingPass, setEditingPass] = useState(null)

  const { setCurrentPage } = usePagination()

  const loadData = useCallback(async () => {
    if (!organizationUUID) {
      hideLoading()
      return
    }

    showLoading()
    try {
      const passStatusParam = passStatus === 'all' ? '' : passStatus
      const [passesRes, templatesRes] = await Promise.all([
        getAccessPasses(organizationUUID, { status: passStatusParam }),
        getAccessPassTemplates(organizationUUID),
      ])
      // Season seat passes are auto-generated; hide them from this admin list.
      const allPasses = passesRes?.data?.data || []
      setPasses(allPasses.filter((pass) => pass.type !== 'season_seat'))
      setTemplates(templatesRes?.data?.data || [])
    } catch (error) {
      console.error(error)
    } finally {
      hideLoading()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationUUID, passStatus])

  useEffect(() => {
    loadData()
  }, [loadData])

  useEffect(() => {
    setCurrentPage(0)
  }, [passStatus, setCurrentPage])

  const handleExportHolders = () => {
    if (!passes.length) return
    exportData(
      passes.map(toAccessPassHolderExportRow),
      'access_pass_holders',
      undefined,
      undefined,
      undefined,
      organization?.name
    )
  }

  const handleDeleteTemplate = async (template) => {
    const confirmed = window.confirm(
      `Delete template "${template.name}"? It will be disabled and existing passes will not be affected.`
    )
    if (!confirmed) return

    try {
      await deleteAccessPassTemplate({
        templateId: template.uuid || template.id,
      })
      await loadData()
    } catch (error) {
      console.error(error)
      window.alert('Failed to delete template.')
    }
  }

  const handleEnableTemplate = async (template) => {
    try {
      await updateAccessPassTemplate({
        templateId: template.uuid || template.id,
        disabled: false,
      })
      await loadData()
    } catch (error) {
      console.error(error)
      window.alert('Failed to enable template.')
    }
  }

  const openEditTemplate = (template) => {
    setEditingTemplate(template)
    setShowCreateTemplate(true)
  }

  const closeTemplateModal = (show) => {
    setShowCreateTemplate(show)
    if (!show) {
      setEditingTemplate(null)
    }
  }

  const openEditPass = (pass) => {
    setEditingPass(pass)
  }

  const closeEditPassModal = (show) => {
    if (!show) {
      setEditingPass(null)
    }
  }

  const handleTabSelect = (tabKey) => {
    if (!tabKey) return
    setActiveTab(tabKey)
    setCurrentPage(0)
  }

  const passColumns = useMemo(
    () => [
      {
        key: 'name',
        label: 'Pass',
        searchValue: (pass) => pass.name,
      },
      {
        key: 'code',
        label: 'Code',
        searchValue: (pass) => pass.checkInCode,
      },
      {
        key: 'holder',
        label: 'Holder',
        searchValue: (pass) =>
          [
            holderDisplayName(pass),
            holderEmail(pass),
            pass?.holder?.phoneNumber,
            pass?.users_permissions_user?.phoneNumber,
          ]
            .filter(Boolean)
            .join(' ') || 'Unassigned',
      },
      {
        key: 'phone',
        label: 'Phone',
        searchValue: (pass) =>
          pass?.holder?.phoneNumber ||
          pass?.users_permissions_user?.phoneNumber ||
          '',
      },
      {
        key: 'events',
        label: 'Events',
        className: 'sm',
        searchValue: (pass) => pass.events?.length || 0,
      },
      {
        key: 'status',
        label: 'Status',
        className: 'sm',
        searchValue: (pass) => pass.status,
      },
      {
        key: 'actions',
        label: '',
        className: 'xs',
        searchValue: () => '',
      },
    ],
    []
  )

  const templateColumns = useMemo(
    () => [
      {
        key: 'name',
        label: 'Template',
        searchValue: (template) => template.name,
      },
      {
        key: 'events',
        label: 'Events',
        className: 'sm',
        searchValue: (template) => template.events?.length || 0,
      },
      {
        key: 'online',
        label: 'Online',
        className: 'sm',
        searchValue: (template) =>
          template.availableOnline
            ? template.isVisible === false
              ? 'hidden'
              : 'available'
            : 'off',
      },
      {
        key: 'actions',
        label: '',
        className: 'xs',
        searchValue: () => '',
      },
    ],
    []
  )

  if (isLoading) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  return (
    <section className="spacer-sm" data-testid="access-passes-wrapper-content">
      <header className="section-heading--flex section-header-sm">
        <div className="section-heading">
          <h1>Access Passes</h1>
          <p className="section-header-desc">
            Create reusable templates, then issue passes to users with one
            click.
          </p>
        </div>
        <Stack direction="horizontal" gap={2}>
          <LinkContainer to="/access-pass-holders">
            <Button size="lg" variant="outline-light">
              Holders report
            </Button>
          </LinkContainer>
          <Button
            size="lg"
            variant="outline-light"
            disabled={!passes.length}
            onClick={handleExportHolders}
          >
            Export holders
          </Button>
          <Button
            size="lg"
            variant="outline-primary"
            onClick={() => setShowCreateTemplate(true)}
          >
            Create template
          </Button>
          <Button size="lg" onClick={() => setShowCreate(true)}>
            Create pass
          </Button>
        </Stack>
      </header>

      <Tabs
        activeKey={activeTab}
        onSelect={handleTabSelect}
        variant="pills"
        className="nav-pills-default w-max-content mb-4"
      >
        <Tab eventKey="passes" title={`Issued passes (${passes.length})`}>
          {activeTab === 'passes' ? (
            <PaginatedTablePanel
              items={passes}
              query={passQuery}
              setQuery={setPassQuery}
              searchPlaceholder="Search by name, code, email, or phone"
              emptyTitle="No access passes yet."
              noResultsTitle="No passes match your search."
              columns={passColumns}
              filters={
                <FloatingLabel controlId="pass-status" label="Status">
                  <Form.Select
                    value={passStatus}
                    onChange={(e) => setPassStatus(e.target.value)}
                    style={{ minWidth: '140px' }}
                  >
                    {PASS_STATUS_OPTS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </FloatingLabel>
              }
              renderRow={(pass) => (
                <tr className="md-x md-x--scroll" key={pass.id || pass.uuid}>
                  <td className="text-left fw-medium">{pass.name}</td>
                  <td className="text-left">
                    <code className="small">{pass.checkInCode}</code>
                  </td>
                  <td className="text-left">
                    <div className="fw-medium">
                      {capitalizeString(
                        holderDisplayName(pass) || 'Unassigned'
                      )}
                    </div>
                    <div className="small text-secondary">
                      {holderEmail(pass) || '—'}
                    </div>
                  </td>
                  <td className="text-left whitespace-nowrap">
                    {pass?.holder?.phoneNumber ||
                    pass?.users_permissions_user?.phoneNumber
                      ? formatPhoneNumber(
                          pass?.holder?.phoneNumber ||
                            pass?.users_permissions_user?.phoneNumber
                        )
                      : '—'}
                  </td>
                  <td className="text-left">{pass.events?.length || 0}</td>
                  <td className="text-left">
                    <Badge
                      bg={pass.status === 'revoked' ? 'secondary' : 'success'}
                    >
                      {pass.status === 'revoked' ? 'Revoked' : 'Active'}
                    </Badge>
                  </td>
                  <td className="text-right">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => openEditPass(pass)}
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              )}
            />
          ) : null}
        </Tab>

        <Tab eventKey="templates" title={`Templates (${templates.length})`}>
          {activeTab === 'templates' ? (
            <PaginatedTablePanel
              items={templates}
              query={templateQuery}
              setQuery={setTemplateQuery}
              searchPlaceholder="Search by template name"
              emptyTitle="No pass templates yet."
              emptyDescription="Create a template to save a reusable set of events."
              noResultsTitle="No templates match your search."
              columns={templateColumns}
              renderRow={(template) => (
                <tr
                  className="md-x md-x--scroll"
                  key={template.id || template.uuid}
                >
                  <td className="text-left fw-medium">{template.name}</td>
                  <td className="text-left">{template.events?.length || 0}</td>
                  <td className="text-left">
                    {template.disabled ? (
                      <Badge bg="secondary">Disabled</Badge>
                    ) : !template.availableOnline ? (
                      <Badge bg="secondary">Off</Badge>
                    ) : template.isVisible === false ? (
                      <Badge bg="warning">Hidden</Badge>
                    ) : (
                      <Badge bg="success">Available</Badge>
                    )}
                  </td>
                  <td className="text-right">
                    <Stack
                      direction="horizontal"
                      gap={2}
                      className="justify-content-end"
                    >
                      {!template.disabled ? (
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => openEditTemplate(template)}
                        >
                          Edit
                        </Button>
                      ) : null}
                      <Button
                        size="sm"
                        variant={
                          template.disabled
                            ? 'outline-success'
                            : 'outline-danger'
                        }
                        onClick={() =>
                          template.disabled
                            ? handleEnableTemplate(template)
                            : handleDeleteTemplate(template)
                        }
                      >
                        {template.disabled ? 'Enable' : 'Delete'}
                      </Button>
                    </Stack>
                  </td>
                </tr>
              )}
            />
          ) : null}
        </Tab>
      </Tabs>

      <CreateAccessPassModal
        show={showCreate}
        setShow={setShowCreate}
        organizationUUID={organizationUUID}
        onCreated={loadData}
      />

      <CreateAccessPassTemplateModal
        show={showCreateTemplate}
        setShow={closeTemplateModal}
        organizationUUID={organizationUUID}
        template={editingTemplate}
        onSaved={loadData}
      />

      <EditAccessPassModal
        show={!!editingPass}
        setShow={closeEditPassModal}
        organizationUUID={organizationUUID}
        pass={editingPass}
        onSaved={loadData}
      />
    </section>
  )
}
